// Browser-only axios client for the React app. Reads the auth token
// from a cookie, attaches Mixpanel distinct-id + tenant headers, and
// hard-redirects to /login on 401. Anything in this file that reaches
// for `window`, `document.cookie`, or `localStorage` is the reason it's
// browser-only.
//
// The SSR equivalent lives in src/api/serverApi.ts — a slimmer Node
// fetch helper used by Vike's +data.ts loaders. Keep the two in sync
// for shared concerns (request shape, base URL resolution) but don't
// try to merge them: the cross-cutting concerns differ (cookies vs.
// inbound Host header, Mixpanel vs. nothing, browser redirect vs.
// thrown error).
import * as Sentry from '@sentry/react';
import axios, { AxiosInstance } from 'axios';
import { omitBy } from 'lodash';
import { deleteCookie, getCookie } from 'src/utils/cookie.util';
import { clearLocalStorage, removeLocalStorage } from 'src/utils/storage.util';
import { httpMethods, ORGANIZATION_KEY, routePaths, TOKEN_KEY } from 'utils/constants';
import { headerType } from 'utils/proptypes';
import { getDistinctId, resetUser } from 'src/analytics/mixpanel';
import urls from './urls';

const axiosApi: AxiosInstance = axios.create();

let siteId: string | null = null;
let headerOrganizationId: string | null = null;

export const setSiteId = (id: string) => {
   siteId = id;
};

export const setHeaderOrganizationId = (id: string) => {
   headerOrganizationId = id;
};

// Registered ONCE, at module load. These used to be registered inside execute(),
// i.e. on every single request: after N requests every subsequent request ran N
// request handlers and 2N response handlers, and one 401 fired the
// clear-storage-and-redirect branch N times. siteId / headerOrganizationId are
// module-level and read inside the callback at request time, so hoisting is
// behaviour-preserving.
axiosApi.interceptors.request.use(config => {
   // Just add when have value
   if (siteId) {
      config.headers['x-site-id'] = siteId;
   }

   if (headerOrganizationId) {
      config.headers['organization-id'] = headerOrganizationId;
   }

   // Forward the Mixpanel distinct id so backend-emitted events attribute
   // to the same identity (anonymous or logged-in) as frontend events.
   const distinctId = getDistinctId();
   if (distinctId) {
      config.headers['x-mp-distinct-id'] = distinctId;
   }

   return config;
});

/**
 * A failed API call used to leave no trace anywhere: the interceptor handled 401
 * and re-threw everything else, and most callers have no `onError`, so a 500 on
 * a save was invisible in Sentry, in Mixpanel, and to the traveler.
 *
 * What gets captured, and what deliberately does not:
 *   - 5xx and network/timeout failures are ours to fix, so they are exceptions;
 *   - 4xx is the API telling a caller it asked wrong (validation, not-found,
 *     rate limits) — those become breadcrumbs, so they show up as context on a
 *     later error without drowning the issue stream;
 *   - 401 is the session expiring, which the redirect below already handles.
 *
 * Fingerprinted by method + route + status rather than by message, so one broken
 * endpoint is one issue instead of one per URL.
 */
type ApiFailure = {
   code?: string;
   config?: { method?: string; url?: string };
   response?: { status?: number; data?: { errors?: { type?: string }[] } };
};

const reportApiError = (err: ApiFailure) => {
   const status: number | undefined = err?.response?.status;
   const method = String(err?.config?.method ?? 'get').toUpperCase();
   // Strip ids out of the path so `/hotel/123/rates` and `/hotel/456/rates`
   // fingerprint as the same endpoint.
   const url: string = err?.config?.url ?? '';
   // UUIDs first: a numeric-segment pass run first eats the leading digits of
   // one and leaves `/:idf0a2b3c-...` behind.
   const route = url
      .split('?')[0]
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:uuid')
      .replace(/\/\d+(?=\/|$)/g, '/:id');

   if (status === 401) return;

   if (status && status < 500) {
      Sentry.addBreadcrumb({
         category: 'api',
         level: 'warning',
         message: `${method} ${route} → ${status}`,
         data: { type: err?.response?.data?.errors?.[0]?.type },
      });
      return;
   }

   Sentry.captureException(err, {
      tags: { source: 'api', route, status: status ? String(status) : 'network' },
      fingerprint: ['api', method, route, status ? String(status) : 'network'],
      contexts: { api: { method, route, status: status ?? null, code: err?.code } },
   });
};

/** The response-error interceptor, exported so it can be tested without reaching into axios internals. */
export const handleResponseError = (err: any) => {
   reportApiError(err);

   if (err.response?.status === 401) {
      // Read before the cookie goes: only a 401 that ends a real session signs anyone out.
      // A logged-out visitor's 401, or the second of several parallel 401s, finds no token.
      const hadSession = !!getCookie(TOKEN_KEY);
      deleteCookie(TOKEN_KEY);
      removeLocalStorage(ORGANIZATION_KEY);
      // NPT-1909: the traveller is signed out on either branch below, so the Mixpanel
      // identity has to go too. clearLocalStorage deliberately keeps the mp_ keys, and
      // AuthContext's logout (the only other resetUser caller) never runs on this path, so
      // without this the next person on the device is tracked as the expired user. Only when
      // there was a session, though: resetting for a visitor who never signed in would throw
      // away the anonymous id their pre-login events are stitched to.
      if (hadSession) resetUser();

      const errorType = err.response?.data?.errors?.[0]?.type;
      if (errorType !== 'ACCOUNT_DISABLED') {
         clearLocalStorage();
         deleteCookie(TOKEN_KEY);

         // Browser-only redirect. On the server (SSR), the
         // surrounding render code is responsible for catching
         // the rejected promise and translating it into an HTTP
         // 302 response. Calling window.location.href on the
         // server would crash the renderer.
         if (typeof window !== 'undefined') {
            window.location.href = `${window.location.origin}${routePaths.LOGIN_PAGE}`;
         }
      }
   }

   return Promise.reject(err);
};

axiosApi.interceptors.response.use(res => res, handleResponseError);

const BaseApi = () => {
   const execute = async (
      method: string,
      endpoint: string,
      headers: headerType = {},
      body: object = {},
      isFile = false,
      params: object = {},
      responseType?: string,
      signal?: AbortSignal,
   ) => {
      axiosApi.defaults.baseURL = urls.baseUrl;

      const token = getCookie(TOKEN_KEY);

      const requestConfig: any = {
         withCredentials: true,
         headers: {
            Accept: 'application/json',
            'Content-Type': isFile ? 'multipart/form-data' : 'application/json',
            ...headers,
            Authorization: token ? `Bearer ${token}` : undefined,
         },
         timeout: 180000,
         responseType,
         signal,
      };

      if (method === httpMethods.GET) {
         requestConfig.params = omitBy(body, value => value === null || value === undefined);
         return axiosApi.get(endpoint, requestConfig);
      } else if (method === httpMethods.POST) {
         return axiosApi.post(endpoint, body, requestConfig);
      } else if (method === httpMethods.PUT) {
         return axiosApi.put(endpoint, body, requestConfig);
      } else if (method === httpMethods.PATCH) {
         requestConfig.params = params;
         return axiosApi.patch(endpoint, body, requestConfig);
      } else {
         return axiosApi.delete(endpoint, requestConfig);
      }
   };

   const get = async (endpoint: string, params?: any, headers?: any, isFile?: any, signal?: AbortSignal) =>
      execute(httpMethods.GET, endpoint, headers, params, isFile, {}, undefined, signal);
   const post = async (endpoint: string, body?: any, headers?: any, isFile?: any, signal?: AbortSignal) =>
      execute(httpMethods.POST, endpoint, headers, body, isFile, {}, undefined, signal);
   const put = async (endpoint: string, body?: any, headers?: any, isFile?: any) => execute(httpMethods.PUT, endpoint, headers, body, isFile);
   const patch = async (endpoint: string, body: any, headers?: any, isFile?: any, params?: any) =>
      execute(httpMethods.PATCH, endpoint, headers, body, isFile, params);
   const del = async (endpoint: string, body?: any, headers?: any, isFile?: any) => execute(httpMethods.DELETE, endpoint, headers, body, isFile);

   return {
      methods: httpMethods,
      urls,
      execute,
      get,
      post,
      put,
      patch,
      del,
   };
};

export default BaseApi;
